import React from "react"
import styled, { css, keyframes } from "styled-components"
import { connect } from "react-redux"
import { AutoSuggest, ButtonLink } from "buoy-core"
import { responsive, colors } from "buoy-theme"
import Source from "../Source"
import { captureException } from "../../common/error"
import { onSubmit as _onSubmit, onChange as _onChange } from "../handlers"
import { setSearchActive, diagnosisInfoSubmit } from "../../search/actions"
import { coronavirusSearch } from "../../integration/utils"
import tracker from "../../common/tracker"
import { inIframe } from "../../common/common"

const SearchOuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: none;
  width: 100%;
  opacity: 1;
  padding-left: 0;
  position: relative;
  margin-top: -20px;

  > * {
    width: 100%;
    max-width: 496px;
    margin-left: auto;
    margin-right: auto;
  }

  ${props =>
    props.focused
      ? css`
          position: ${props => (props.editorActive ? "absolute" : "fixed")};
          margin: 0;
          height: 100%;
          left: 0;
          right: 0;
          overflow-y: scroll;
          box-sizing: border-box;
          padding: 0px 16px 80px;
          // NOTE: For COVID-19 banner - TODO: delete conditional when banner is removed
          ${
            props.forCovid19 && !props.iFrame
              ? responsive("top", "105px", "100px", "95px", "95px", "95px")
              : responsive("top", "68px", "68px", "59px", "59px", "56px")
          }

          // NOTE: Quick fix for part of interview showing when diag search was active on specific screen sizes
          ${
            props.forCovid19 && !props.iFrame
              && css`
                  @media (max-width: 374px) {
                    top: 120px;
                  }
                `
           }
          
          background: ${props => (props.postIntent ? colors.primary : "white")};
          animation: ${fade} 0.4s ease;

          ${props =>
            props.postIntent
              ? css`
                  div > div {
                    background: ${colors.primary};

                    &:hover {
                      background: ${colors.primary};
                    }
                  }
                  p {
                    color: white;
                  }
                `
              : ""};

          form {
            min-height: 48px;
            button {
              color: ${props.postIntent ? "white" : colors.neutralDarkest};
            }
          }
        `
      : css`
          form {
            margin: 12px 0 8px;
          }
        `};

  form + p:nth-child(2) {
    padding-left: 16px;
    ${responsive("margin-top", "20px", "20px", "20px", "10px")};
  }

  form + div:nth-child(2),
  p + div:nth-child(3) {
    height: auto;
    box-sizing: border-box;
  }

  ${props =>
    !props.focused &&
    `
    animation: ${fade1} 0.4s ease;
  `};
`

const fade = keyframes`
  0%  { opacity: 0 }
  100%  { opacity: 1 }
`

const fade1 = keyframes`
  0%,1%  { opacity: 0 }
  100%  { opacity: 1 }
`

const Skip = styled(ButtonLink).attrs({
  color: "neutralDarkest",
  fontSize: "bodyMain",
})`
  float: right;
`

class DiagnosisSearch extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      suggestions: [],
      active: -1,
      searchFocus: false,
      attempted: false,
    }
  }

  componentDidMount() {
    tracker.dxocSearchShown()
    if (typeof document !== "undefined") {
      const input = document.getElementById("search-input")
      input.addEventListener("keydown", this.keyDownInput, false)
      input.addEventListener("focus", this.onFocus, false)
    }
  }

  componentWillUnmount() {
    if (typeof document !== "undefined") {
      const input = document.getElementById("search-input")
      input.removeEventListener("keydown", this.keyDownInput, false)
      input.removeEventListener("focus", this.onFocus, false)
    }
  }

  keyDownInput = e => {
    let key = e.keyCode || e.which
    switch (key) {
      case 13: // Return
        e.preventDefault()
        e.stopPropagation()
        if (this.state.active >= 0) {
          this.handleSuggestionClick(
            this.state.suggestions[this.state.active].token
          )
        } else if (this.props.value.length > 1) {
          this.props.onSubmitDiagnosisConcern()
        }
        break
      case 38: // Arrow up
        e.preventDefault()
        e.stopPropagation()
        this.decrement()
        break
      case 40: // Arrown down
        e.preventDefault()
        e.stopPropagation()
        this.increment()
        break
      default:
        break
    }
  }

  increment() {
    if (this.state.suggestions.length) {
      let index =
        this.state.active >= this.state.suggestions.length - 1
          ? 0
          : this.state.active + 1
      this.setState({ active: index })
    }
  }

  decrement() {
    if (this.state.suggestions.length) {
      let index =
        this.state.active <= 0
          ? this.state.suggestions.length - 1
          : this.state.active - 1
      this.setState({ active: index })
    }
  }

  handleChange = e => {
    e.preventDefault()
    e.stopPropagation()
    let query = e.target.value
    if (!this.state.attempted) {
      tracker.dxocSearchAttempted()
      this.setState({ attempted: true })
    }
    this.props.onChange(query)
    if (query === "") {
      this.setState({ suggestions: [] })
    }
    if (query.length > 1) {
      Source.diagnosisSearch(query, "INTERVIEW_START_DIAGNOSIS_CONCERN")
        .then(data => {
          let suggestions = []
          // For Coronavirus project
          let coronavirus = coronavirusSearch(query.toLowerCase())
          if (coronavirus) {
            suggestions.push({
              text: "Coronavirus",
              token: "Coronavirus",
            })
          }

          data.interpretations.forEach(diagnosis => {
            if (!coronavirusSearch(diagnosis.toLowerCase())) {
              suggestions.push({
                text: diagnosis.toProperCase(),
                token: diagnosis,
              })
            }
          })
          this.setState({ suggestions })
        })
        .catch(captureException)
    } else if (query.length === 0) this.setState({ attempted: false })
  }

  handleSuggestionClick = name => {
    tracker.dxocSearchClickedOnSuggestion()

    this.props.onChange(name)
    this.setState({ suggestions: [], active: -1, searchFocus: false })
    this.props.onSubmitDiagnosisConcern()
  }

  handleClear = () => {
    this.props.onChange("")
    this.setState({ suggestions: [] })
    document.getElementById("search-input").focus()
  }

  onFocus = () => {
    if (!this.state.searchFocus) tracker.dxocSearchClickedOnSearchBar()
    this.props.setSearchActive(true)
    this.setState({ searchFocus: true, attempted: false })
  }

  onCancel = () => {
    tracker.dxocSearchCancel()
    this.props.setSearchActive(false)
    this.setState({ searchFocus: false, attempted: false })
  }

  render() {
    const { searchFocus, suggestions, active } = this.state
    const { placeholder, item, onSkip, noSkip, value } = this.props
    let iFrame = inIframe()
    return (
      <React.Fragment>
        <SearchOuterContainer
          focused={item.noFocus ? false : searchFocus}
          {...this.props}
          iFrame={iFrame}
        >
          <AutoSuggest
            placeholder={placeholder || item.placeholder}
            suggestions={suggestions}
            suggestionClick={this.handleSuggestionClick}
            value={value}
            showCancel={item.noFocus ? false : searchFocus}
            onCancel={this.onCancel}
            selected={0}
            activeSuggestion={active}
            onChange={this.handleChange}
            onClear={this.handleClear}
            setListRef={() => () => true}
            setSuggestionRef={() => () => true}
            showDefaults={false}
            showSuggestionHeader={false}
          />
        </SearchOuterContainer>
        {!noSkip && !suggestions.length && <Skip onClick={onSkip}>Skip</Skip>}
      </React.Fragment>
    )
  }
}

const mapStateToProps = state => ({
  value: state.integration.diagnosisConcernSearch,
  forCovid19: state.showCovid19AlertBanner,
})

const mapDispatchToProps = (dispatch, ownProps) => {
  const onSubmit = _onSubmit(ownProps.item, dispatch)
  const onChange = _onChange(ownProps.item, dispatch)

  return {
    onSubmit,
    onSubmitDiagnosisConcern: () => {
      dispatch(diagnosisInfoSubmit(ownProps.postIntent))
    },
    onChange: name => onChange(name),
    onSkip: () => {
      tracker.dxocSearchSkip()
      onChange("")
      onSubmit()
    },
    setSearchActive: state => {
      dispatch(setSearchActive(state))
    },
  }
}

const DiagnosisSearchWrapper = connect(
  mapStateToProps,
  mapDispatchToProps
)(DiagnosisSearch)

export { DiagnosisSearchWrapper as default, DiagnosisSearch }



// WEBPACK FOOTER //
// ./assets/interview/components/DiagnosisSearch.js